import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { createAppRouter } from './router';
import {
  clearPrivateQueryCache,
  queryClient,
} from '@/cache/queryClient';
import { notifySessionEnded } from '@/cache/data-revision-coordinator';
import { deleteLegacyPersistedQueryCache } from '@/cache/persister';
import { setUnauthorizedHandler } from '@/api/client';
import { isPublicPath, isSessionPublicPath } from '@/lib/public-paths';
import { resumeFromUrl } from '@/lib/oauth-consent';
import { ErrorBoundary } from '@/components/error-boundary';
import { addBreadcrumb, initTelemetry } from '@/lib/telemetry';
import {
  installReleaseRouter,
  noteChunkLoadFailure,
  noteReleasePageResolved,
  startReleaseChecker,
} from '@/lib/app-release';
import '@/styles/styles.css';
import '@/styles/launch-progress.css';
import '@/styles/spa-overrides.css';
import '@/styles/responsive.css';

// Install global error/rejection handlers + telemetry flush lifecycle early, so
// crashes during initial render are still captured.
initTelemetry();
void deleteLegacyPersistedQueryCache();

// Every router default — the pending skeleton and its delays, the hover-preload
// delay, the freshness authority, the error panel — lives in router.ts, where
// router.test.ts asserts each one on the router this app actually builds.
const router = createAppRouter(queryClient);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Breadcrumbs came only from API and network calls, so a crash trail read as a
// list of requests with no indication of where the user was when it happened —
// and a render crash that fires no request left no trail at all. The route
// TEMPLATE, never the resolved URL: a concrete path carries entity ids.
router.subscribe('onResolved', ({ toLocation }) => {
  const matched = router.state.matches.at(-1)?.routeId ?? toLocation.pathname;
  addBreadcrumb('route', String(matched));
  // A page change clears the release store's edit latch (lib/app-release.ts).
  noteReleasePageResolved(toLocation.pathname);
});

// A newer release: the root route's `beforeLoad` and the hidden-tab reload read the router
// through the store, and the store reads `version.json` (lib/app-release.ts, 2026-10-21).
installReleaseRouter(router);
// A lazily loaded chunk that fails to load is often a deploy that replaced it. Not
// `preventDefault()`: that would resolve the import to `undefined`, and both callers destructure
// it. The import still rejects into their own error handling; this only asks `version.json` at
// once.
window.addEventListener('vite:preloadError', () => noteChunkLoadFailure());

setUnauthorizedHandler(() => {
  // The consent page (`/connect`) is a public path that needs a session: any 401 there sends the
  // person to sign in, with the way back to the request (`?next=connect&request=<id>`) when the
  // URL names a well-formed one. It cannot wait for its own reads to fail: the purge below
  // removes them mid-flight, and a removed query never settles.
  const needsSession = typeof window !== 'undefined' && isSessionPublicPath(window.location.pathname);
  // The way back, when this page is one a sign-in resumes: a consent request, or a creative's
  // files page (the link Claude hands a person).
  const resume = typeof window !== 'undefined' ? resumeFromUrl(window.location.href) : null;
  // Don't yank logged-out users off the public token screens — a 401 from a
  // background query (e.g. prefetch) must not abort an in-progress invite/reset.
  if (typeof window !== 'undefined' && (needsSession || !isPublicPath(window.location.pathname))) {
    // Cookies are shared across tabs, so an expired/revoked session is shared
    // state too. Announce the logout before navigation; sibling tabs purge and
    // reload fail-closed instead of retaining scoped rows until their next 401.
    notifySessionEnded();
    void clearPrivateQueryCache().then(() => (resume
      ? router.navigate({ to: '/login', search: resume })
      : router.navigate({ to: '/login' })));
    return;
  }
  void clearPrivateQueryCache();
});

const rootEl = document.getElementById('root')!;
createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
startReleaseChecker();
